import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import { Button } from 'components/ui/button';
import Skeleton from 'components/ui/skeleton';
import { IS_IO } from 'lib/site';
import { planGoals, planShared, useStudyPlan } from 'modules/studyPlan';
import { StudyStreakCard } from 'modules/studyStreak';
import { GoalCard } from './GoalCard';
import { GoalSheet } from './GoalSheet';
import { HowBuiltCard } from './HowBuiltCard';
import { IeltsTrackCard } from './IeltsTrackCard';
import { ReminderCard } from './ReminderCard';
import { RoadmapCard } from './RoadmapCard';
import { TopicsCard } from './TopicsCard';
import { WeekCard } from './WeekCard';

interface SheetState {
  open: boolean;
  /** Bumped on every open so the form restarts from the saved goals. */
  key: number;
  /** The exam whose answers the form opens at, when one is missing an answer. */
  focusExam?: string;
}

/**
 * Settings > Study plan: the goals, a roadmap per goal, this week, and topic mastery per goal. `?goal=edit`
 * (with `&exam=` to start at one exam) opens the goal form, so other screens can send the learner straight to it.
 */
export default function StudyPlanPage() {
  const { t } = useTranslation();
  const { data: plan, isLoading, isError, refetch } = useStudyPlan();
  const [params, setParams] = useSearchParams();
  const [sheet, setSheet] = useState<SheetState>({ open: false, key: 0 });
  const openSheet = (focusExam?: string) => setSheet((s) => ({ open: true, key: s.key + 1, focusExam }));

  const wantsEdit = params.get('goal') === 'edit';
  const examParam = params.get('exam') ?? undefined;
  useEffect(() => {
    if (!wantsEdit || isLoading) return;
    setSheet((s) => ({ open: true, key: s.key + 1, focusExam: examParam }));
    // Once open, the link has done its job; a reload should not open the form again.
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      next.delete('goal');
      next.delete('exam');
      return next;
    }, { replace: true });
  }, [wantsEdit, examParam, isLoading, setParams]);

  if (isLoading) {
    return (
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]" aria-hidden="true">
        <div className="flex flex-col gap-4">
          <Skeleton className="h-64 rounded-lg" />
          <Skeleton className="h-72 rounded-lg" />
        </div>
        <Skeleton className="hidden h-64 rounded-lg lg:block" />
      </div>
    );
  }

  // A failed background refresh keeps the plan (and an open form); only a first load has nothing to show.
  if (isError && !plan) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card px-5 py-4">
        <span className="inline-flex items-center gap-2 text-[13px] text-error-700 dark:text-error-400">
          <AlertCircle size={16} aria-hidden="true" />
          {t('failed_to_load')}
        </span>
        <Button variant="outline" size="xs" onClick={() => refetch()}>{t('retry')}</Button>
      </div>
    );
  }

  const goals = plan ? planGoals(plan) : [];
  const shared = plan ? planShared(plan) : { dailyStudyMinutes: 0, targetPercent: 0 };
  const several = goals.length > 1;

  return (
    <>
      {isError && (
        <div role="status" className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-border bg-card px-5 py-3">
          <span className="inline-flex items-center gap-2 text-[13px] text-error-700 dark:text-error-400">
            <AlertCircle size={16} aria-hidden="true" />
            {t('failed_to_load')}
          </span>
          <Button variant="outline" size="xs" onClick={() => refetch()}>{t('retry')}</Button>
        </div>
      )}
      {plan ? (
        <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="flex min-w-0 flex-col gap-4">
            <GoalCard goals={goals} daysLeft={plan.goal.daysLeft} {...shared} onEdit={openSheet} />
            {!IS_IO && <StudyStreakCard />}
            {goals.map((goal) => (
              <React.Fragment key={goal.exam}>
                <RoadmapCard roadmap={goal.roadmap} exam={several ? goal.exam : undefined} />
                {goal.exam === 'IELTS' && <IeltsTrackCard roadmap={goal.roadmap} />}
              </React.Fragment>
            ))}
            <WeekCard week={plan.week} />
            {goals.map((goal) => (
              <TopicsCard key={goal.exam} mastery={goal.mastery} targetPercent={shared.targetPercent} exam={several ? goal.exam : undefined} />
            ))}
          </div>
          <div className="flex min-w-0 flex-col gap-4">
            <HowBuiltCard goals={goals} {...shared} />
            <ReminderCard />
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
        <section aria-labelledby="plan-empty" className="rounded-lg border border-border bg-card p-6">
          <h2 id="plan-empty" className="font-display text-xl font-bold tracking-[-0.02em] text-foreground">{t('studyPlan.no_goal_title')}</h2>
          <p className="mt-2 max-w-xl text-sm leading-relaxed text-muted-foreground">{t('studyPlan.no_goal_body')}</p>
          <Button className="mt-5" onClick={() => openSheet()}>{t('studyPlan.set_goal')}</Button>
        </section>
        {!IS_IO && <StudyStreakCard />}
        </div>
      )}

      <GoalSheet
        key={sheet.key}
        open={sheet.open}
        onOpenChange={(open) => setSheet((s) => ({ ...s, open }))}
        goals={goals}
        shared={plan ? shared : null}
        focusExam={sheet.focusExam}
      />
    </>
  );
}
